<script setup>
import {
  computed,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
  watch,
} from "vue";
import dayjs from "dayjs";
import { useRoute } from "vue-router";
import apiClient from "@/apiClient.js";
import { apiUrl } from "@/vars";
import SearchableInput from "@/components/SearchableInput.vue";
import { useAuthStore } from "@/stores/AuthStore";
import {
  ArrowPathIcon,
  CheckCircleIcon,
  EyeIcon,
  MagnifyingGlassIcon,
  ShieldCheckIcon,
  TrashIcon,
  XMarkIcon,
} from "@heroicons/vue/24/solid";

const auth = useAuthStore();
const route = useRoute();
const invoices = ref([]);
const summary = ref({});
const loading = ref(false);
const saving = ref(false);
const activeInvoice = ref(null);
const selectedSupplier = ref(null);
const supplierInputKey = ref(0);
const search = ref("");
const state = ref("needs_review");
const paidDate = ref(dayjs().format("YYYY-MM-DD"));
let pollTimer = null;

const canManage = computed(() =>
  auth.hasAnyRole(["finance-department", "super"]),
);
const supplierOutput = (supplier) => supplier?.company ?? "";
const filters = [
  { value: "needs_review", label: "Needs review" },
  { value: "unpaid", label: "To pay" },
  { value: "due_soon", label: "Due soon" },
  { value: "overdue", label: "Overdue" },
  { value: "paid", label: "Paid" },
  { value: "vat_ready", label: "VAT ready" },
  { value: "blocked", label: "AI held" },
  { value: "all", label: "All" },
];
const vatTreatments = [
  ["standard_20", "20% VAT"],
  ["no_vat", "No VAT"],
  ["zero_rated", "Zero rated"],
  ["exempt", "Exempt"],
  ["reverse_charge", "Reverse charge"],
  ["needs_review", "Needs review"],
];
const form = reactive({
  supplier_id: null,
  invoice_date: "",
  due_date: "",
  payment_date: "",
  invoice_number: "",
  description: "",
  currency: "GBP",
  gross_amount: "",
  net_amount: "",
  vat_amount: "",
  vat_treatment: "needs_review",
  category: "",
  analysis: "",
  notes: "",
});

const money = (minorUnits, currency = "GBP") => {
  const code = String(currency || "GBP").toUpperCase();
  try {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: code,
    }).format(Number(minorUnits || 0) / 100);
  } catch {
    return `${code} ${(Number(minorUnits || 0) / 100).toFixed(2)}`;
  }
};
const moneyInput = (pence) =>
  pence === null || pence === undefined ? "" : (Number(pence) / 100).toFixed(2);
const formatDate = (value) => (value ? dayjs(value).format("D MMM YYYY") : "—");
const viewUrl = (invoice) =>
  `${apiUrl}/purchase-invoices/${invoice.id}/download?inline=1`;

const status = (invoice) => {
  if (invoice.vat_ready)
    return ["VAT ready", "bg-emerald-100 text-emerald-800"];
  if (invoice.paid_at) return ["Paid", "bg-emerald-100 text-emerald-800"];
  if (invoice.extraction_status === "blocked")
    return ["AI held", "bg-amber-100 text-amber-900"];
  if (["queued", "processing"].includes(invoice.extraction_status))
    return ["Reading", "bg-cyan-100 text-cyan-800"];
  if (invoice.extraction_status === "failed")
    return ["Scan failed", "bg-rose-100 text-rose-800"];
  if (invoice.evidence_status !== "approved")
    return ["Needs review", "bg-amber-100 text-amber-900"];
  if (invoice.due_date && dayjs(invoice.due_date).isBefore(dayjs(), "day"))
    return ["Overdue", "bg-rose-100 text-rose-800"];
  return ["To pay", "bg-sky-100 text-sky-800"];
};

const load = async () => {
  loading.value = true;
  try {
    const [listResponse, summaryResponse] = await Promise.all([
      apiClient.get("/api/purchase-invoices", {
        params: { state: state.value, search: search.value || undefined },
      }),
      apiClient.get("/api/purchase-invoices-summary"),
    ]);
    invoices.value = Array.isArray(listResponse.data) ? listResponse.data : [];
    summary.value = summaryResponse.data ?? {};
    if (activeInvoice.value?.id) {
      const refreshed = invoices.value.find(
        (invoice) => invoice.id === activeInvoice.value.id,
      );
      if (refreshed) openInvoice(refreshed);
    }
  } finally {
    loading.value = false;
  }
};

const openInvoice = (invoice) => {
  activeInvoice.value = invoice;
  selectedSupplier.value = invoice.supplier ?? null;
  Object.assign(form, {
    supplier_id: invoice.supplier_id ?? null,
    invoice_date: invoice.invoice_date ?? "",
    due_date: invoice.due_date ?? "",
    payment_date: invoice.payment_date ?? "",
    invoice_number: invoice.invoice_number ?? "",
    description: invoice.description ?? "",
    currency: invoice.currency ?? "GBP",
    gross_amount: moneyInput(invoice.gross_amount),
    net_amount: moneyInput(invoice.net_amount),
    vat_amount: moneyInput(invoice.vat_amount),
    vat_treatment: invoice.vat_treatment ?? "needs_review",
    category: invoice.category ?? "",
    analysis: invoice.analysis ?? "",
    notes: invoice.notes ?? "",
  });
  paidDate.value =
    invoice.paid_at ?? invoice.payment_date ?? dayjs().format("YYYY-MM-DD");
  supplierInputKey.value++;
};

const closeInvoice = () => {
  activeInvoice.value = null;
  selectedSupplier.value = null;
  if (pollTimer) window.clearTimeout(pollTimer);
};
const selectSupplier = (supplier) => {
  selectedSupplier.value = supplier;
  form.supplier_id = supplier?.id ?? null;
  if (supplier?.default_vat_treatment)
    form.vat_treatment = supplier.default_vat_treatment;
  if (supplier?.default_vat_category)
    form.category = supplier.default_vat_category;
  if (supplier?.default_vat_analysis)
    form.analysis = supplier.default_vat_analysis;
};

const refreshActive = async () => {
  if (!activeInvoice.value?.id) return;
  const { data } = await apiClient.get(
    `/api/purchase-invoices/${activeInvoice.value.id}`,
  );
  openInvoice(data);
};
const save = async () => {
  saving.value = true;
  try {
    const payload = {
      ...form,
      supplier_id: selectedSupplier.value?.id ?? null,
    };
    const { data } = await apiClient.put(
      `/api/purchase-invoices/${activeInvoice.value.id}`,
      payload,
    );
    openInvoice(data);
    await load();
  } catch (error) {
    alert(
      error?.response?.data?.message ?? "Unable to save the supplier invoice.",
    );
  } finally {
    saving.value = false;
  }
};
const rescan = async () => {
  saving.value = true;
  try {
    const { data } = await apiClient.post(
      `/api/purchase-invoices/${activeInvoice.value.id}/rescan`,
    );
    openInvoice(data);
    const poll = async () => {
      await refreshActive();
      if (
        ["queued", "processing"].includes(
          activeInvoice.value?.extraction_status,
        )
      ) {
        pollTimer = window.setTimeout(poll, 2500);
      } else {
        await load();
      }
    };
    pollTimer = window.setTimeout(poll, 2500);
  } catch (error) {
    alert(
      error?.response?.data?.message ?? "Unable to start invoice extraction.",
    );
  } finally {
    saving.value = false;
  }
};
const approve = async () => {
  if (
    activeInvoice.value?.duplicate_of_purchase_invoice_id &&
    !confirm(
      "This file matches an earlier invoice. Confirm that this is genuinely payable?",
    )
  )
    return;
  saving.value = true;
  try {
    const { data } = await apiClient.put(
      `/api/purchase-invoices/${activeInvoice.value.id}/approve`,
      {
        confirm_duplicate: Boolean(
          activeInvoice.value?.duplicate_of_purchase_invoice_id,
        ),
      },
    );
    openInvoice(data);
    await load();
  } catch (error) {
    const errors = error?.response?.data?.errors;
    alert(
      errors
        ? Object.values(errors).flat().join("\n")
        : (error?.response?.data?.message ?? "Unable to approve this invoice."),
    );
  } finally {
    saving.value = false;
  }
};
const markPaid = async () => {
  if (!paidDate.value) return;
  saving.value = true;
  try {
    const { data } = await apiClient.put(
      `/api/purchase-invoices/${activeInvoice.value.id}/paid`,
      { paid_at: paidDate.value },
    );
    openInvoice(data);
    await load();
  } catch (error) {
    alert(
      error?.response?.data?.message ?? "Unable to mark this invoice paid.",
    );
  } finally {
    saving.value = false;
  }
};
const markUnpaid = async () => {
  if (!confirm("Move this invoice back to the unpaid list?")) return;
  const { data } = await apiClient.delete(
    `/api/purchase-invoices/${activeInvoice.value.id}/paid`,
  );
  openInvoice(data);
  await load();
};
const deleteInvoice = async () => {
  const invoice = activeInvoice.value;
  if (!invoice?.id) return;
  if (
    !confirm(
      `Permanently delete supplier invoice #${invoice.id}${invoice.invoice_number ? ` (${invoice.invoice_number})` : ""}? This cannot be undone.`,
    )
  )
    return;

  saving.value = true;
  try {
    await apiClient.delete(`/api/purchase-invoices/${invoice.id}`);
    closeInvoice();
    await load();
  } catch (error) {
    alert(
      error?.response?.data?.message ??
        "Unable to delete this supplier invoice.",
    );
  } finally {
    saving.value = false;
  }
};

watch(state, load);
onMounted(async () => {
  await load();
  const linkedInvoiceId = Number(route.query.invoice_id);
  if (!Number.isInteger(linkedInvoiceId) || linkedInvoiceId <= 0) return;

  try {
    const { data } = await apiClient.get(
      `/api/purchase-invoices/${linkedInvoiceId}`,
    );
    openInvoice(data);
  } catch (error) {
    alert(
      error?.response?.data?.message ??
        "Unable to load the linked supplier invoice.",
    );
  }
});
onBeforeUnmount(() => pollTimer && window.clearTimeout(pollTimer));
</script>

<template>
  <div class="space-y-4">
    <form-box header="Supplier Invoices">
      <div class="flex flex-col gap-4">
        <div
          class="rounded-xl border border-cyan-200 bg-cyan-50 px-4 py-3 text-sm text-cyan-950 dark:border-cyan-900 dark:bg-cyan-950/30 dark:text-cyan-100"
        >
          Suppliers can email <strong>invoices@medic1.co.uk</strong>. First-time
          or suspicious senders are held here without using AI credits; approved
          senders are read automatically.
        </div>
        <div class="flex flex-wrap gap-2">
          <button
            v-for="filter in filters"
            :key="filter.value"
            class="btn text-sm"
            :class="state === filter.value ? 'accent' : ''"
            @click.prevent="state = filter.value"
          >
            {{ filter.label }}
            <span class="ml-1 opacity-70">{{
              summary[filter.value] ?? 0
            }}</span>
          </button>
        </div>
        <form class="flex gap-2" @submit.prevent="load">
          <input
            v-model="search"
            class="input min-w-0 flex-1"
            placeholder="Search supplier, invoice number or service"
          />
          <button class="btn accent" :disabled="loading">
            <MagnifyingGlassIcon class="icon" /> Search
          </button>
          <button class="btn" :disabled="loading" @click.prevent="load">
            <ArrowPathIcon
              class="icon"
              :class="loading ? 'animate-spin' : ''"
            />
          </button>
        </form>
      </div>
    </form-box>

    <div class="box overflow-x-auto">
      <table class="min-w-full text-sm">
        <thead>
          <tr class="text-left text-xs uppercase text-slate-500">
            <th class="px-3 py-2">Status</th>
            <th class="px-3 py-2">Supplier</th>
            <th class="px-3 py-2">Invoice</th>
            <th class="px-3 py-2">Service</th>
            <th class="px-3 py-2">Due</th>
            <th class="px-3 py-2 text-right">Total</th>
            <th class="px-3 py-2">Source</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="invoice in invoices"
            :key="invoice.id"
            class="border-t border-slate-200 dark:border-slate-700"
          >
            <td class="px-3 py-3">
              <span
                class="rounded-full px-2 py-1 text-xs font-semibold"
                :class="status(invoice)[1]"
                >{{ status(invoice)[0] }}</span
              >
            </td>
            <td class="px-3 py-3">
              <div class="font-medium">
                {{ invoice.supplier?.company || "Unmatched supplier" }}
              </div>
              <div class="text-xs text-slate-500">
                {{ invoice.source_sender?.email || "Manual upload" }}
              </div>
            </td>
            <td class="px-3 py-3">
              <div>{{ invoice.invoice_number || "—" }}</div>
              <div class="text-xs text-slate-500">
                {{ formatDate(invoice.invoice_date) }}
              </div>
            </td>
            <td class="max-w-xs truncate px-3 py-3">
              {{ invoice.description || "—" }}
            </td>
            <td
              class="px-3 py-3"
              :class="
                !invoice.paid_at &&
                invoice.due_date &&
                dayjs(invoice.due_date).isBefore(dayjs(), 'day')
                  ? 'font-semibold text-rose-700'
                  : ''
              "
            >
              {{ formatDate(invoice.due_date) }}
            </td>
            <td class="px-3 py-3 text-right font-semibold">
              {{ money(invoice.gross_amount, invoice.currency) }}
            </td>
            <td class="px-3 py-3">
              <RouterLink
                v-if="invoice.source_ticket_id"
                class="text-cyan-700 hover:underline"
                :to="`/tickets/${invoice.source_ticket_id}`"
                >Ticket #{{ invoice.source_ticket_id }}</RouterLink
              ><span v-else>Upload</span>
            </td>
            <td class="px-3 py-3 text-right">
              <button class="btn text-xs" @click="openInvoice(invoice)">
                <EyeIcon class="icon" /> Review
              </button>
            </td>
          </tr>
          <tr v-if="!loading && !invoices.length">
            <td colspan="8" class="px-3 py-8 text-center text-slate-500">
              No supplier invoices in this section.
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div
      v-if="activeInvoice"
      class="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4"
      @click.self="closeInvoice"
    >
      <div
        class="max-h-[94vh] w-full max-w-7xl overflow-y-auto rounded-2xl bg-white shadow-2xl dark:bg-slate-900"
      >
        <div
          class="sticky top-0 z-10 flex items-center justify-between border-b border-slate-200 bg-white px-5 py-4 dark:border-slate-700 dark:bg-slate-900"
        >
          <div>
            <h2 class="text-lg font-semibold">
              Supplier invoice #{{ activeInvoice.id }}
            </h2>
            <p class="text-xs text-slate-500">
              {{ activeInvoice.file_name || "Missing attachment" }}
            </p>
          </div>
          <button class="btn" @click="closeInvoice">
            <XMarkIcon class="icon" />
          </button>
        </div>
        <div class="grid gap-5 p-5 lg:grid-cols-2">
          <div
            class="min-h-[520px] overflow-hidden rounded-xl border border-slate-200 bg-slate-100 dark:border-slate-700 dark:bg-slate-950"
          >
            <iframe
              v-if="activeInvoice.has_file"
              class="h-[70vh] w-full"
              :src="viewUrl(activeInvoice)"
              title="Invoice evidence"
            ></iframe>
            <div
              v-else
              class="flex h-full items-center justify-center p-8 text-center text-slate-500"
            >
              No attachment was supplied. Open the source ticket to contact the
              sender.
            </div>
          </div>
          <div class="space-y-4">
            <div
              v-if="activeInvoice.extraction_error"
              class="rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-100"
            >
              {{ activeInvoice.extraction_error }}
            </div>
            <div
              v-if="activeInvoice.duplicate_of"
              class="rounded border border-rose-200 bg-rose-50 p-3 text-sm text-rose-900"
            >
              Possible duplicate of invoice #{{ activeInvoice.duplicate_of.id }}
              {{ activeInvoice.duplicate_of.invoice_number || "" }}.
            </div>
            <div class="grid gap-3 sm:grid-cols-2">
              <div class="sm:col-span-2">
                <label class="text-xs uppercase text-slate-500">Supplier</label>
                <div
                  v-if="selectedSupplier"
                  class="input flex items-center justify-between"
                >
                  <span>{{ selectedSupplier.company }}</span
                  ><button
                    class="text-xs text-rose-600"
                    @click="selectSupplier(null)"
                  >
                    Remove
                  </button>
                </div>
                <SearchableInput
                  v-else
                  :key="supplierInputKey"
                  url="/api/supplier/search?query="
                  :output="supplierOutput"
                  placeholder="Search suppliers"
                  @selected="selectSupplier"
                />
              </div>
              <div>
                <label class="text-xs uppercase text-slate-500"
                  >Invoice date</label
                ><input
                  v-model="form.invoice_date"
                  type="date"
                  class="input w-full"
                />
              </div>
              <div>
                <label class="text-xs uppercase text-slate-500">Due date</label
                ><input
                  v-model="form.due_date"
                  type="date"
                  class="input w-full"
                />
              </div>
              <div>
                <label class="text-xs uppercase text-slate-500"
                  >Payment date</label
                ><input
                  v-model="form.payment_date"
                  type="date"
                  class="input w-full"
                />
              </div>
              <div>
                <label class="text-xs uppercase text-slate-500"
                  >Invoice number</label
                ><input v-model="form.invoice_number" class="input w-full" />
              </div>
              <div>
                <label class="text-xs uppercase text-slate-500"
                  >VAT treatment</label
                ><select v-model="form.vat_treatment" class="input w-full">
                  <option
                    v-for="option in vatTreatments"
                    :key="option[0]"
                    :value="option[0]"
                  >
                    {{ option[1] }}
                  </option>
                </select>
              </div>
              <div>
                <label class="text-xs uppercase text-slate-500">Currency</label
                ><input
                  v-model.trim="form.currency"
                  class="input w-full uppercase"
                  maxlength="3"
                  list="supplier-invoice-currencies"
                  placeholder="GBP"
                />
                <datalist id="supplier-invoice-currencies">
                  <option value="GBP"></option>
                  <option value="USD"></option>
                  <option value="EUR"></option>
                  <option value="CAD"></option>
                  <option value="AUD"></option>
                  <option value="CHF"></option>
                  <option value="JPY"></option>
                </datalist>
              </div>
              <div class="sm:col-span-2">
                <label class="text-xs uppercase text-slate-500"
                  >Service / description</label
                ><input v-model="form.description" class="input w-full" />
              </div>
              <div>
                <label class="text-xs uppercase text-slate-500"
                  >Gross ({{ form.currency || "GBP" }})</label
                ><input
                  v-model="form.gross_amount"
                  type="number"
                  step="0.01"
                  class="input w-full"
                />
              </div>
              <div>
                <label class="text-xs uppercase text-slate-500"
                  >Net ({{ form.currency || "GBP" }})</label
                ><input
                  v-model="form.net_amount"
                  type="number"
                  step="0.01"
                  class="input w-full"
                />
              </div>
              <div>
                <label class="text-xs uppercase text-slate-500"
                  >VAT ({{ form.currency || "GBP" }})</label
                ><input
                  v-model="form.vat_amount"
                  type="number"
                  step="0.01"
                  class="input w-full"
                />
              </div>
              <div>
                <label class="text-xs uppercase text-slate-500">Category</label
                ><input v-model="form.category" class="input w-full" />
              </div>
              <div>
                <label class="text-xs uppercase text-slate-500">Analysis</label
                ><input v-model="form.analysis" class="input w-full" />
              </div>
              <div class="sm:col-span-2">
                <label class="text-xs uppercase text-slate-500">Notes</label
                ><textarea
                  v-model="form.notes"
                  class="input min-h-[70px] w-full"
                ></textarea>
              </div>
            </div>
            <div
              v-if="canManage"
              class="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 pt-4 dark:border-slate-700"
            >
              <div class="flex flex-wrap gap-2">
                <button
                  class="btn text-rose-700"
                  :disabled="saving"
                  @click="deleteInvoice"
                >
                  <TrashIcon class="icon" /> Delete
                </button>
                <button
                  class="btn"
                  :disabled="saving || !activeInvoice.has_file"
                  @click="rescan"
                >
                  <ArrowPathIcon class="icon" /> Scan with flagship AI</button
                ><button class="btn" :disabled="saving" @click="save">
                  Save details</button
                ><button
                  v-if="activeInvoice.evidence_status !== 'approved'"
                  class="btn accent"
                  :disabled="saving"
                  @click="approve"
                >
                  <ShieldCheckIcon class="icon" /> Approve
                </button>
              </div>
              <div
                v-if="activeInvoice.evidence_status === 'approved'"
                class="flex items-center gap-2"
              >
                <template v-if="!activeInvoice.paid_at"
                  ><input v-model="paidDate" type="date" class="input" /><button
                    class="btn accent"
                    :disabled="saving || !paidDate"
                    @click="markPaid"
                  >
                    <CheckCircleIcon class="icon" /> Mark paid
                  </button></template
                ><template v-else
                  ><span class="text-sm font-semibold text-emerald-700"
                    >Paid {{ formatDate(activeInvoice.paid_at) }} · VAT
                    ready</span
                  ><button class="btn text-xs" @click="markUnpaid">
                    Undo
                  </button></template
                >
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
